import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Clock, MapPin, Users, Trash2, Flag, X, DollarSign, Camera, Loader2, Image as ImageIcon, Share2 } from "lucide-react";
import { toast } from "sonner";
import { fullUrl } from "@/components/app/ImageUploader";
import StoriesViewer from "@/components/app/StoriesViewer";
import CameraCapture from "@/components/app/CameraCapture";
import { isAppBusy } from "@/lib/busy";
import { AvatarStack, PeopleList } from "@/components/app/UserAvatar";
import { reportTarget } from "@/lib/report";
import { Store } from "lucide-react";

function GoingAvatars({ people, total }) {
  if (!people?.length) return null;
  return <div className="mt-4"><AvatarStack people={people} total={total} /></div>;
}

const TYPE_LABEL = {
  churrasco: "Churrasco", resenha: "Resenha", festa: "Festa", atletica: "Atlética/República",
  luau: "Luau", show: "Show", feira: "Feira", outro: "Outro",
};

function formatDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" });
  } catch { return iso; }
}

export default function EventSheet({ eventId, initial, onClose, onChanged, onNearbyAdegas, resumePhoto }) {
  const { user } = useAuth();
  const [ev, setEv] = useState(initial);
  const [items, setItems] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [posts, setPosts] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [cameraOpen, setCameraOpen] = useState(!!resumePhoto);
  const [storyIndex, setStoryIndex] = useState(null); // when !== null, open stories

  const load = async () => {
    try {
      const { data } = await api.get(`/events/${eventId}`);
      setEv(data);
      const [its, pps] = await Promise.all([
        api.get(`/events/${eventId}/items`),
        api.get(`/events/${eventId}/posts`),
      ]);
      setItems(its.data);
      setPosts(pps.data);
      if (data.is_mine || data.my_rsvp === "going") {
        try {
          const { data: rr } = await api.get(`/events/${eventId}/rsvps`);
          setRsvps(rr);
        } catch { setRsvps([]); }
      } else setRsvps([]);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [eventId]); // eslint-disable-line

  const rsvp = async (status) => {
    if (!user) { toast.error("Entre pra confirmar presença."); return; }
    try {
      const { data } = await api.post(`/events/${eventId}/rsvp`, { status });
      setEv(data);
      await load();
      onChanged?.();
      toast.success(status === "going" ? "Confirmado! 🎉" : status === "maybe" ? "Marcado como talvez" : "Ok, você não vai");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!newItem.trim()) return;
    try {
      await api.post(`/events/${eventId}/items`, { name: newItem });
      setNewItem("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const claim = async (itemId) => {
    try { await api.post(`/events/${eventId}/items/${itemId}/claim`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const removeItem = async (itemId) => {
    try { await api.delete(`/events/${eventId}/items/${itemId}`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancelar este rolê? Os confirmados serão avisados.")) return;
    try { await api.post(`/events/${eventId}/cancel`); toast.success("Evento cancelado."); onChanged?.(); onClose?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const report = () => reportTarget("event", eventId, "este rolê");

  const uploadPost = async (url) => {
    const { data } = await api.post(`/events/${eventId}/posts`, { image_url: url });
    console.info("[foto] resposta do servidor (post)", data?.post_id || data);
    toast.success("Foto postada!");
    await load();
    onChanged?.();
  };

  const removePost = async (postId) => {
    if (!window.confirm("Apagar esta foto?")) return;
    try { await api.delete(`/events/${eventId}/posts/${postId}`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const share = async () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/share/${eventId}`;
    const title = `${ev.name} · Enkosta`;
    const text = `${ev.name} em Marília · confira o rolê no Enkosta`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiado! Cole no WhatsApp.");
      }
    } catch { /* user cancelled */ }
  };

  if (!ev) return null;

  const isMine = ev.is_mine;
  const confirmed = ev.my_rsvp === "going";
  const canPost = ev.is_live && !ev.cancelled && (confirmed || isMine);
  const coverUrl = fullUrl(ev.cover_url);

  return (
    <>
    <Sheet open onOpenChange={(o)=>{ if (!o) onClose?.(); }}>
      <SheetContent side="bottom" onFocusOutside={(e) => e.preventDefault()} onPointerDownOutside={(e) => { if (isAppBusy() || cameraOpen) e.preventDefault(); }} onEscapeKeyDown={(e) => { if (isAppBusy()) e.preventDefault(); }} className="max-h-[80dvh] overflow-y-auto rounded-t-3xl bg-card border-t border-white/10 p-0" data-testid="event-sheet">
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2 bg-card/95 backdrop-blur">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-white/20 absolute left-1/2 -translate-x-1/2 top-2" />
          <span />
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-muted grid place-items-center press ml-auto" data-testid="close-sheet"><X className="w-4 h-4" /></button>
        </div>
        {coverUrl && (
          <div className="relative aspect-[16/9] w-full overflow-hidden">
            <img src={coverUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-card" />
          </div>
        )}
        <div className="p-5 pb-32">
          {/* Stories CTA at top — hard to miss */}
          {posts.length > 0 && (
            <button
              onClick={() => setStoryIndex(0)}
              className="w-full flex items-center gap-3 p-3 rounded-2xl bg-gradient-to-r from-secondary/25 via-primary/20 to-secondary/25 border border-secondary/50 shadow-lg mb-4 press"
              data-testid="stories-top-cta"
            >
              <div className="relative shrink-0">
                <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-secondary via-primary to-secondary animate-spin-slow" />
                <div className="absolute inset-[2px] rounded-full bg-card" />
                <div className="relative m-[4px] h-14 w-14 rounded-full overflow-hidden">
                  <img src={fullUrl(posts[0].image_url)} alt="" className="h-full w-full object-cover" />
                </div>
              </div>
              <div className="flex-1 text-left">
                <div className="text-base font-bold heading flex items-center gap-2">
                  Ver histórias
                  <span className="relative inline-flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-secondary"></span></span>
                </div>
                <div className="text-xs text-muted-foreground">{posts.length} foto{posts.length === 1 ? "" : "s"} do rolê — toque pra assistir</div>
              </div>
              <div className="h-10 w-10 rounded-full bg-secondary text-secondary-foreground grid place-items-center shrink-0 font-bold">▶</div>
            </button>
          )}

          <SheetHeader className="text-left">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Badge variant="outline" className="text-xs border-white/15 text-muted-foreground">{TYPE_LABEL[ev.type]}</Badge>
                  {ev.is_live && <Badge className="bg-secondary text-secondary-foreground">Rolando agora</Badge>}
                  {ev.has_posts && (
                    <button onClick={()=>setStoryIndex(0)} className="press" data-testid="open-stories-badge">
                      <Badge variant="outline" className="border-secondary text-secondary hover:bg-secondary/10 cursor-pointer">● {ev.posts_count} foto{ev.posts_count === 1 ? "" : "s"}</Badge>
                    </button>
                  )}
                  {ev.cancelled && <Badge variant="destructive">Cancelado</Badge>}
                </div>
                <SheetTitle className="text-2xl heading font-extrabold leading-tight" data-testid="event-title">{ev.name}</SheetTitle>
                <p className="text-xs text-muted-foreground mt-1">por {ev.creator?.nickname}</p>
              </div>
              <button onClick={share} className="h-9 w-9 rounded-full bg-primary text-primary-foreground grid place-items-center press shrink-0" data-testid="share-event-button" aria-label="Compartilhar">
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </SheetHeader>

          {ev.description && <p className="mt-4 text-sm text-foreground/90 leading-relaxed">{ev.description}</p>}

          <div className="mt-5 grid grid-cols-1 gap-2.5 text-sm">
            <div className="flex items-center gap-3"><Clock className="w-4 h-4 text-primary" /> {formatDate(ev.date)} · {ev.start_time} – {ev.end_time}</div>
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-primary" />
              {confirmed || isMine
                ? <span>{ev.address || "Endereço indicado no mapa"}</span>
                : <span className="text-muted-foreground italic">Endereço exato liberado após confirmar presença</span>}
            </div>
            <div className="flex items-center gap-3"><Users className="w-4 h-4 text-primary" /> {ev.confirmed_count} confirmado{ev.confirmed_count === 1 ? "" : "s"}{ev.max_people ? ` · limite ${ev.max_people}` : ""}</div>
            {ev.price && <div className="flex items-center gap-3"><DollarSign className="w-4 h-4 text-primary" /> {ev.price}</div>}
          </div>

          <GoingAvatars people={ev.going_preview} total={ev.confirmed_count} />

          {!ev.cancelled && (
            <div className="mt-5 grid grid-cols-3 gap-2">
              <Button variant={ev.my_rsvp === "going" ? "default" : "outline"} onClick={()=>rsvp("going")} className="h-12 press" data-testid="rsvp-going">Eu vou</Button>
              <Button variant={ev.my_rsvp === "maybe" ? "default" : "outline"} onClick={()=>rsvp("maybe")} className="h-12 press" data-testid="rsvp-maybe">Talvez</Button>
              <Button variant={ev.my_rsvp === "no" ? "default" : "outline"} onClick={()=>rsvp("no")} className="h-12 press" data-testid="rsvp-no">Não vou</Button>
            </div>
          )}

          {/* Who's going */}
          {(isMine || confirmed) && rsvps.length > 0 && (
            <div className="mt-6">
              <div className="text-sm font-semibold mb-2">Quem vai ({rsvps.length})</div>
              <PeopleList people={rsvps} testid="event-people" onReport={(p) => reportTarget("avatar", p.user_id, "esta foto de perfil")} />
            </div>
          )}
          {!isMine && !confirmed && ev.confirmed_count > 0 && (
            <p className="mt-6 text-xs text-muted-foreground" data-testid="people-hidden-note">Confirme presença pra ver quem vai.</p>
          )}

          {/* Bring list */}
          <div className="mt-6">
            <div className="text-sm font-semibold mb-2">O que levar</div>
            {items.length === 0 && <p className="text-xs text-muted-foreground">Nenhum item na lista ainda.</p>}
            <ul className="space-y-2">
              {items.map((it) => {
                const missing = !it.claimed_by;
                const mine = it.claimed_by === user?.id;
                return (
                  <li key={it.item_id} className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 border ${missing ? "border-secondary/50 bg-secondary/5" : "border-white/10 bg-muted/60"}`} data-testid={`item-${it.item_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{it.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {it.claimed_nickname ? `com ${it.claimed_nickname}` : "faltando"}
                      </div>
                    </div>
                    {(confirmed || isMine) && !ev.cancelled && (
                      <button onClick={()=>claim(it.item_id)} disabled={!!it.claimed_by && !mine} className="px-3 h-9 rounded-md bg-primary text-primary-foreground text-xs font-semibold disabled:opacity-40 press" data-testid={`claim-${it.item_id}`}>
                        {mine ? "Desmarcar" : "Eu levo"}
                      </button>
                    )}
                    {isMine && (
                      <button onClick={()=>removeItem(it.item_id)} className="h-9 w-9 grid place-items-center text-muted-foreground press"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </li>
                );
              })}
            </ul>
            {isMine && !ev.cancelled && (
              <form onSubmit={addItem} className="mt-3 flex gap-2">
                <Input value={newItem} onChange={(e)=>setNewItem(e.target.value)} placeholder="ex: gelo, carvão..." data-testid="new-item-input" />
                <Button type="submit" className="press" data-testid="add-item-button">Adicionar</Button>
              </form>
            )}
            <button onClick={() => onNearbyAdegas?.(ev.lat ?? ev.approx_lat, ev.lng ?? ev.approx_lng)} className="mt-3 w-full flex items-center justify-center gap-2 h-11 rounded-xl border border-primary/40 bg-primary/5 text-sm font-semibold press" data-testid="nearby-adegas-button">
              <Store className="w-4 h-4 text-primary" /> Ver adegas perto daqui
            </button>
          </div>

          <div className="mt-8 flex items-center justify-between">
            {isMine && !ev.cancelled ? (
              <Button variant="destructive" onClick={cancel} className="press" data-testid="cancel-event-button">Cancelar evento</Button>
            ) : <span />}
            {!isMine && (
              <button onClick={report} className="text-xs text-muted-foreground flex items-center gap-1 press" data-testid="report-event-button">
                <Flag className="w-3.5 h-3.5" /> Denunciar
              </button>
            )}
          </div>
        </div>

        {/* Posts section */}
        <div className="px-5 pb-10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-secondary" />
              <h3 className="text-sm font-semibold">Momentos do rolê</h3>
              {ev.has_posts && <span className="relative inline-flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-secondary"></span></span>}
            </div>
            {canPost && (
              <button onClick={() => setCameraOpen(true)} className="flex items-center gap-1.5 px-3 h-9 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold press" data-testid="post-photo-button">
                <Camera className="w-3.5 h-3.5" />
                Postar foto
              </button>
            )}
          </div>
          {!ev.is_live && posts.length === 0 && (
            <p className="text-xs text-muted-foreground">As fotos aparecem aqui durante o rolê, postadas por quem confirmou presença.</p>
          )}
          {ev.is_live && !confirmed && !isMine && (
            <p className="text-xs text-muted-foreground">Confirme presença pra postar momentos do rolê.</p>
          )}
          {posts.length === 0 && ev.is_live && (confirmed || isMine) && (
            <p className="text-xs text-muted-foreground">Seja o primeiro a postar uma foto!</p>
          )}
          {posts.length > 0 && (
            <div>
              {/* Story bubble */}
              <button onClick={()=>setStoryIndex(0)} className="flex items-center gap-3 w-full p-2.5 rounded-xl bg-gradient-to-r from-secondary/15 to-primary/10 border border-secondary/30 mb-3 press" data-testid="stories-ring-button">
                <div className="relative shrink-0">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-secondary via-primary to-secondary animate-pulse" />
                  <div className="absolute inset-[2px] rounded-full bg-card" />
                  <div className="relative m-[4px] h-10 w-10 rounded-full overflow-hidden grid place-items-center bg-muted">
                    <img src={fullUrl(posts[0].image_url)} alt="" className="h-full w-full object-cover" />
                  </div>
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-semibold">Ver histórias</div>
                  <div className="text-[11px] text-muted-foreground">{posts.length} foto{posts.length === 1 ? "" : "s"} · toque pra ver tudo</div>
                </div>
              </button>

              {/* Grid */}
              <div className="grid grid-cols-3 gap-1.5">
                {posts.map((p, i) => {
                  const src = fullUrl(p.image_url);
                  const canDelete = user && (p.user_id === user.id || isMine || user.role === "admin");
                  return (
                    <div key={p.post_id} className="relative aspect-square rounded-lg overflow-hidden bg-muted" data-testid={`event-post-${p.post_id}`}>
                      <img src={src} alt="" onClick={() => setStoryIndex(i)} className="absolute inset-0 w-full h-full object-cover cursor-zoom-in" />
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-1.5">
                        <div className="text-[10px] text-white font-medium truncate">{p.nickname}</div>
                      </div>
                      {canDelete && (
                        <button onClick={(e) => { e.stopPropagation(); removePost(p.post_id); }} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 grid place-items-center press" data-testid={`delete-post-${p.post_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <CameraCapture open={cameraOpen} onClose={() => setCameraOpen(false)} onUploaded={uploadPost} context={{ event_id: eventId }} resume={resumePhoto} label="Postar momento do rolê" />
      </SheetContent>
    </Sheet>
    {storyIndex !== null && posts.length > 0 && (
      <StoriesViewer
        posts={posts}
        startIndex={storyIndex}
        canDelete={(p) => user && (p.user_id === user.id || isMine || user.role === "admin")}
        onClose={() => setStoryIndex(null)}
        onDeleted={() => load()}
      />
    )}
    </>
  );
}
